import React, { useEffect, useState } from "react";
import {
    Box,
    Grid,
    TextareaAutosize,
    CircularProgress,
    TextField
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Modal from "../common/modal/Modal";
import ButtonComponent from "../common/button/Button";
import TextInput from "../common/TextInput";
import Dropdown from "../common/Dropdown";
import { postSupportTicketEndpoint } from "../../common/endpoints";
import { postSupportTicketSlice } from "../../redux/actions/postSupportTicketSlice";
import { toast } from "react-toastify";
import ExpandableFunctionDropdown from "./ExpandableFunctionDropdown";

const AppAdmin = [
    "Planning",
    "PO Exception Management",
    "Admin",
    "Reports",
    "KPI Metrics",
    "Alerts",
    "Announcements",
    "Training Content",
    "Settings & Config",
    "Extract Forecast SAP",
    "Forecast History",
    "Forecast Extraction Calendar",
];

const GlobalPlanner = [
    "Planning",
    "PO Exception Management",
    "Reports",
    "KPI Metrics",
    "Alerts",
    "Announcements",
    "Extract Forecast SAP",
    "Forecast History",
];

const RegionalPlanner = [
    "Planning",
    "PO Exception Management",
    "Reports",
    "KPI Metrics",
    "Alerts",
    "Announcements",
    "Forecast History",
];

const SupplierAdmin = [
    "Planning",
    "PO Exception Management",
    "Reports",
    "Admin",
    "Announcements",
    "Training Content",
    "Forecast History",
];

const SupplierPlanner = [
    "Planning",
    "PO Exception Management",
    "Reports",
    "Announcements",
    "Training Content",
    "Forecast History",
];

const GlobalViewer = [
    "Planning",
    "Forecast History",
];

const RegionalViewer = [
    "Planning",
    "Forecast History",
];

const roleMenusMap = {
    "App Admin": AppAdmin,
    "Global Planner": GlobalPlanner,
    "Regional Planner": RegionalPlanner,
    "Supplier Admin": SupplierAdmin,
    "Supplier Planner": SupplierPlanner,
    "Global Viewer": GlobalViewer,
    "Regional Viewer": RegionalViewer,
};

// Functions available per menu per role for "AL Process" category
// Labels match the actual ButtonComponent label= props in each page component
const menuFunctionsMap = {
    // CPS Planning page — role component map from CPS/index.js:
    // App Admin → CPSBuyerForecast, Global Planner → CPSBuyerRoleForecast,
    // Regional Planner → RegionalPlannerForecast, Global/Regional Viewer → viewer components,
    // Supplier Admin/Planner → CPSSupplierForecast
    "Planning": {
        "App Admin": ["Filter", "Clear Filters", "Save", "Submit", "Upload", "Download", "Create Varient", "Edit Varient", "Delete Varient", "Select Varient", "Share Varient", "Other"],
        "Global Planner": ["Filter", "Clear Filters", "Save", "Submit", "Ad hoc Submit", "Create Varient", "Edit Varient", "Delete Varient", "Select Varient", "Share Varient", "Other"],
        "Regional Planner": ["Filter", "Clear Filters", "Save", "Submit", "Ad hoc Submit", "Create Varient", "Edit Varient", "Delete Varient", "Select Varient", "Share Varient", "Other"],
        "Global Viewer": ["Filter", "Clear Filters", "Save", "Create Varient", "Edit Varient", "Delete Varient", "Select Varient", "Share Varient", "Other"],
        "Regional Viewer": ["Filter", "Clear Filters", "Save", "Create Varient", "Edit Varient", "Delete Varient", "Select Varient", "Share Varient", "Other"],
        "Supplier Admin": ["Filter", "Clear Filters", "Save", "Copy Forecast", "Upload", "Download", "Create Varient", "Edit Varient", "Delete Varient", "Select Varient", "Share Varient", "Other"],
        "Supplier Planner": ["Filter", "Clear Filters", "Save", "Copy Forecast", "Upload", "Download", "Create Varient", "Edit Varient", "Delete Varient", "Select Varient", "Share Varient", "Other"],
    },
    // POExceptionMgmtBuyer (Global/Regional Planner + Viewers) vs POExceptionMgmtSupplier (App Admin, Supplier*)
    // isViewerRole disables Save (column picker) and Send to Supplier for Global/Regional Viewer
    "PO Exception Management": {
        "App Admin": ["Save", "Clear All Filters", "Other"],
        "Global Planner": ["Filter", "Clear Filters", "Apply and Collapse", "Save", "Clear All Filters", "Send to Supplier", "Other"],
        "Regional Planner": ["Filter", "Clear Filters", "Apply and Collapse", "Save", "Clear All Filters", "Send to Supplier", "Other"],
        "Global Viewer": ["Clear Filters", "Apply and Collapse", "Clear All Filters", "Other"],
        "Regional Viewer": ["Clear Filters", "Apply and Collapse", "Clear All Filters", "Other"],
        "Supplier Admin": ["Save", "Clear All Filters", "Other"],
        "Supplier Planner": ["Save", "Clear All Filters", "Other"],
    },
    // SupplierManagement Admin submenus:
    // Manage Users: Invite Users, Add User, Edit User, Delete User, Clear All, Update, Submit
    // Manage Reason Names: Add Reason Name, Edit Reason Name, Delete Reason Name, Save, Update
    "Admin": {

        "App Admin": [
            {
                title: "Manage Users",
                items: [
                    "Invite Users",
                    "Add User",
                    "Edit User",
                    "Delete User",
                    "Back",
                    "Clear All"
                ]
            },
            {
                title: "Manage Reason Names",
                items: [
                    "Add Reason Name",
                    "Edit Reason Name",
                    "Delete Reason Name",
                    "Back",
                    "Clear All"
                ]
            },
            {
                title: "Manage Domain Whitelisting",
                items: [
                    "Add Domain",
                    "Upload Domain",
                    "Whitelist Domain",
                    "Remove Whitelist",
                    "Clear Filter",
                    "Download",
                ]
            },
            {
                title: "Other",
                items: ["Other"]
            }
        ],

        "Supplier Admin": [
            "Invite Users", "Add User", "Edit User", "Delete User", "Back", "Clear All", "Other"],
    },
    // AR Reporting: card-click navigation only — no ButtonComponent actions
    "Reports": {
        "App Admin": ["Forecast Change Audit Log", "Training Content Adoption", "Supplier Forecast Accuracy", "Supplier Forecast Confirmation", "Other"],
        "Global Planner": ["Forecast Change Audit Log", "Training Content Adoption", "Supplier Forecast Accuracy", "Supplier Forecast Confirmation", "Other"],
        "Regional Planner": ["Forecast Change Audit Log", "Training Content Adoption", "Supplier Forecast Accuracy", "Supplier Forecast Confirmation", "Other"],
        // "Supplier Admin": ["View Report"],
        // "Supplier Planner": ["View Report"],
    },
    // KPI Metrics: card-click navigation only
    "KPI Metrics": {
        "App Admin": ["Supplier Forecast Confirmation SLA Tracker", "Supplier Adoption Metrics", "Other"],
        "Global Planner": ["Supplier Forecast Confirmation SLA Tracker", "Supplier Adoption Metrics", "Other"],
        "Regional Planner": ["Supplier Forecast Confirmation SLA Tracker", "Supplier Adoption Metrics", "Other"],
        "Supplier Admin": ["View Report", "Other"],
        "Supplier Planner": ["View Report", "Other"],
    },
    // Alerts: AlertCards expand/collapse; no ButtonComponent buttons
    "Alerts": {
        "App Admin": ["Alert", "Other"],
        "Global Planner": ["Alert", "Other"],
        "Regional Planner": ["Alert", "Other"],
        "Supplier Admin": ["Alert", "Other"],
        "Supplier Planner": ["Alert", "Other"],
    },
    // AdminAnnouncements: Post Announcement, Edit (icon), Delete (icon)
    // SupplierAnnouncements: View More / View Less
    "Announcements": {
        "App Admin": ["Post Announcement", "View Announcement", "Other"],
        "Global Planner": ["View Announcement", "Other"],
        "Regional Planner": ["View Announcement", "Other"],
        "Supplier Admin": ["View Announcement", "Other"],
        "Supplier Planner": ["View Announcement", "Other"],
    },
    // AdminTraining: Add Topic, Save, Update, Edit (icon), Delete (icon)
    // VideoPlayer (supplier): View only
    "Training Content": {
        "App Admin": ["Add Topic", "Save", "Update", "Delete", "Upload", "Other"],
        "Supplier Admin": ["Open Video", "Open Document", "Other"],
        "Supplier Planner": ["Open Video", "Open Document", "Other"],
    },
    // ForecastReviewPeriodConfig + ForecastPublishExclusion:
    // Save, Search, Reset, Add Plant Configuration, Add Restrict Rule,
    // Clear All, Update, Submit, Add Configuration
    "Settings & Config": {
        "App Admin": [
            {
                title: "Forecast Review Period Configuration",
                items: [
                    "Filters",
                    "Add Plant Configuration",
                    "Edit Plant Configuration",
                    "Delete Plant Configuration",
                    "Back",
                    "Clear All"
                ]
            },
            {
                title: "Forecast Publish Restriction",
                items: [
                    "Filters",
                    "Add Restriction",
                    "Edit Restriction",
                    "Delete Restriction",
                    "Back",
                    "Clear All"
                ]
            }]
    },
    // ExtractForecastFromSAP: Clear, Execute
    "Scheduler": {
        "App Admin": [
            {
                title: "Forecast History",
                items: [
                    "Forecast Snapshot Date",
                    "Filters",
                    "Clear All",
                    "Request Download",
                    "My Download Requests",
                    "Refresh"
                ]
            },
            {
                title: "Run Forecast Extraction",
                items: [
                    "Filters",
                    "Clear",
                    "Execute",
                    "Forecast Time Horizon",
                    "Scheduled Job Requests",
                ]
            },
            // 
        ],

"Global Planner": ["Clear", "Execute"],
    },
"Forecast History": {
    "Supplier Planner":
    [
        "Last Customer Update",
        "Filters",
        "Clear All",
        "Request Download",
        "My Download Requests",
        "Refresh"
    ],
        "Supplier Admin": [

            "Last Customer Update",
            "Filters",
            "Clear All",
            "Request Download",
            "My Download Requests",
            "Refresh"

        ]

},
"Forecast Extraction Calendar": {
    "App Admin": ["View Region", "Other"],
    },
"Supplier Confirmation Analytics": {
    "App Admin": ["Open link issue", "Other"],
        "Global Planner": ["Open link issue", "Other"],
            "Regional Planner": ["Open link issue", "Other"],
    },
};

const SUPPORT_CATEGORY = "AL Process";

const ASSIGNED_TO_VALUE = "AL support Team";

const menuLabelAliasMap = {
    "Run Forecast Extraction": "Extract Forecast SAP",
};

const normalizeMenuLabel = (label = "") => menuLabelAliasMap[label] || label;

const normalizeKey = (value = "") => String(value).replace(/[^a-z0-9]/gi, "").toLowerCase();

const pickValueByKeys = (row = {}, keys = []) => {
    const rowEntries = Object.entries(row || {});
    for (const candidateKey of keys) {
        const match = rowEntries.find(([rowKey]) => normalizeKey(rowKey) === normalizeKey(candidateKey));
        if (match) {
            return match[1];
        }
    }
    return undefined;
};

const hasDisplayValue = (value) => !(value === null || value === undefined || value === "");

const SupportTicketModel = (props) => {
    const location = useLocation();
    const dispatch = useDispatch();
    const stateSelector = useSelector((state) => state);
    const [selectedMenu, setSelectedMenu] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [description, setDescription] = useState('');
    const [menuInitialized, setMenuInitialized] = useState(false);
    const [isSubmitClicked, setIsSubmitClicked] = useState(false);
    const [menuOptions, setMenuOptions] = useState([]);
    const roleDetails = localStorage.getItem("userRole");
    const userEmail = localStorage.getItem("userEmail");
    const createTicketLoading = useSelector((state) => state.postSupportTicketSlice);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [fileInputKey, setFileInputKey] = useState(Date.now());

    const SELECTED_DATA_FIELDS = [
        { label: "Material Number", keys: ["materialNumber", "material", "tccMaterialNumber", "tcccMaterialNumber", "materialId"] },
        { label: "Plant Code", keys: ["plantCode", "plant", "plantId", "plantDescription"] },
        { label: "Description", keys: ["description", "materialDescription", "materialDesc"] },
        { label: "Vendor Code", keys: ["vendorCode", "childVendor", "supplierCode", "supplier", "supplierId"] },
        ...(roleDetails === "Supplier Planner" || roleDetails === "Supplier Admin"
            ? [{ label: "Supplier Pin", keys: ["supplierPIN"] }]
            : []),

        ...(roleDetails === "App Admin"
            ? [{ label: "External Material Group", keys: ["scode", "materialGroup", "materialGroupCode"] }]
            : []),
        { label: "Business Category", keys: ["businessCategory", "category"] },
    ];

    const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);

    const [selectedFunction, setSelectedFunction] = useState("");
    const [selectedPriority, setSelectedPriority] = useState("Standard");

    const normalizedSelectedMenu = normalizeMenuLabel((selectedMenu || "").trim());

    const functions =
        menuFunctionsMap[normalizedSelectedMenu]?.[roleDetails] || [];

    const isGrouped =
        functions.length > 0 &&
        typeof functions[0] === "object" &&
        functions[0].items;

    const functionGroups =
        functions.length > 0 && typeof functions[0] === "object"
            ? functions
            : [
                {
                    title: "Functions",
                    items: functions,
                },
            ];
    const priorityOptions = [
        { label: "Standard", value: "Standard", title: "Standard", },
        { label: "Urgent", value: "Urgent", title: "Urgent", },
    ];

    const BLOCKED_EXTENSIONS = [
        "ade", "adp", "app", "asa", "ashx", "asmx", "asp", "bas", "bat",
        "cdx", "cer", "chm", "class", "cmd", "com", "config", "cpl", "crt",
        "csh", "dll", "exe", "fxp", "hlp", "hta", "htr", "htw", "ida",
        "idc", "idq", "inf", "ins", "isp", "its", "jar", "js", "jse", "ksh",
        "lnk", "mad", "maf", "mag", "mam", "maq", "mar", "mas", "mat", "mau",
        "mav", "maw", "mda", "mdb", "mde", "mdt", "mdw", "mdz", "msc", "msh",
        "msh1", "msh1xml", "msh2", "msh2xml", "mshxml", "msi", "msp", "mst",
        "ops", "pcd", "pif", "prf", "prg", "printer", "pst", "reg", "rem",
        "scf", "scr", "sct", "shb", "shs", "shtm", "shtml", "soap", "stm",
        "tmp", "url", "vb", "vbe", "vbs", "vsmacros", "vss", "vst", "vsw",
        "ws", "wsc", "wsf", "wsh", "svg"
    ];

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];

        if (!file) {
            setUploadedFile(null);
            return;
        }

        // 20 MB validation
        const MAX_FILE_SIZE = 20 * 1024 * 1024;

        if (file.size > MAX_FILE_SIZE) {
            toast.error("File size should not exceed 20 MB.");
            event.target.value = "";
            setUploadedFile(null);
            return;
        }

        // Extension validation
        const extension = file.name
            .split(".")
            .pop()
            ?.toLowerCase();

        if (extension && BLOCKED_EXTENSIONS.includes(extension)) {
            toast.error(`.${extension} files are not allowed.`);
            event.target.value = "";
            setUploadedFile(null);
            return;
        }

        setUploadedFile(file);
    };

    const resetForm = () => {
        setSelectedFunction("");
        setDescription("");
        setUploadedFile(null);
        setFileInputKey(Date.now());
    };

    useEffect(() => {
        if (props.showModal) {
            resetForm();
        }
    }, [props.showModal]);

    useEffect(() => {
        const roleLabelsFallback = roleMenusMap[roleDetails] || [];
        const sidebarLabels = (props.menuItems || [])
            .map((item) => item?.label?.trim())
            .filter(Boolean);

        const effectiveLabels =
            (sidebarLabels.length > 0 ? sidebarLabels : roleLabelsFallback)
                .filter(menu => menu !== "Ticket Summary");

        const uniqueMenus = [...new Set(effectiveLabels)];
        setMenuOptions(
            uniqueMenus.map((menu) => ({
                label: menu,
                value: menu,
                title: menu,
            }))
        );
    }, [props.menuItems, location?.pathname, roleDetails])

    useEffect(() => {
        const isMenuValid = !!selectedMenu;
        const isFunctionValid = !!selectedFunction;
        const isPriorityValid = !!selectedPriority;
        const isAssignedToValid = !!ASSIGNED_TO_VALUE;
        const isDescriptionValid = !!description.trim();
        setIsSubmitEnabled(
            isMenuValid &&
            isFunctionValid &&
            isPriorityValid &&
            isAssignedToValid &&
            isDescriptionValid
        );
    }, [selectedMenu, selectedFunction, selectedPriority, description])

    const renderSelectedItemsTable = (items = []) => {
        if (!items.length) return null;
        const visibleFields = SELECTED_DATA_FIELDS.filter((field) =>
            items.some((row) => hasDisplayValue(pickValueByKeys(row, field.keys)))
        );
        if (!visibleFields.length) return null;

        const totalKey = visibleFields.find((field) => field?.label?.toLowerCase?.() === "total")?.label;

        const getStickyStyle = (key) =>
            key === totalKey
                ? {
                    position: "sticky",
                    right: 0,
                    zIndex: 2,
                    background: "#f7f7f7",
                    boxShadow: "-1px 0 0 #d9d9d9",
                }
                : {};

        return (
            <div style={{ margin: '10px 0px', border: '1px solid #bcbcbc', fontSize: 12 }}>
                <div style={{ maxHeight: '180px', overflow: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', width: 'max-content', minWidth: '100%' }}>
                        <thead>
                            <tr>
                                {visibleFields.map((field) => (
                                    <th
                                        key={field.label}
                                        style={{
                                            minWidth: 120,
                                            textAlign: 'left',
                                            padding: '6px 8px',
                                            borderBottom: '1px solid #bcbcbc',
                                            background: '#f3f3f3',
                                            whiteSpace: 'nowrap',
                                            ...getStickyStyle(field.label),
                                        }}
                                    >
                                        {field.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {visibleFields.map((field) => {
                                        const value = pickValueByKeys(row, field.keys);
                                        let displayValue = "-";

                                        if (field.label === "Plant Code") {
                                            const plantCode = pickValueByKeys(row, ["plantCode", "plant", "plantId"]);
                                            const plantDescription = pickValueByKeys(row, ["plantDescription"]);

                                            if (plantCode && plantDescription) {
                                                displayValue = `${plantCode} | ${plantDescription}`;
                                            } else if (plantCode) {
                                                displayValue = plantCode;
                                            } else if (plantDescription) {
                                                displayValue = plantDescription;
                                            }
                                        } else {
                                            const value = pickValueByKeys(row, field.keys);
                                            displayValue =
                                                value === null || value === undefined || value === ""
                                                    ? "-"
                                                    : String(value);
                                        }
                                        return (
                                            <td
                                                key={field.label}
                                                style={{
                                                    minWidth: 120,
                                                    padding: '4px 8px',
                                                    borderBottom: '1px solid #f0f0f0',
                                                    whiteSpace: 'nowrap',
                                                    ...getStickyStyle(field.label),
                                                }}
                                            >
                                                {displayValue}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const merged = (() => {
        const items = props?.selectedItem ?? [];
        if (items.length === 0) return null;
        return (
            <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                <div style={{ margin: 10 }}>{description}</div>
                <strong style={{ marginLeft: 10 }}>Selected Items:</strong>
                <div style={{ marginTop: 8, marginLeft: 10, width: '100%' }}>
                    {renderSelectedItemsTable(items)}
                </div>
            </div>
        );
    })();

    const getUniqueValues = (data = [], key) => {
        if (data?.length > 0) {
            const uniqueValues = [
                ...new Set(data?.map((item) => item[key]).filter(Boolean)),
            ];
            return uniqueValues?.map((value) => ({
                label: value,
                value: value,
                title: value,
            }));
        } else {
            return [];
        }
    };

    const handleSelectedItemsChange = (name, event) => {
        const value = event.target.value;

        if (name === "menu") {
            setSelectedMenu(value);
            setSelectedFunction("");
        }
    };

    useEffect(() => {
        if (!menuInitialized) {
            setDescription("");

            const pathToMenuMap = {
                "/forecastCollabPage": "Planning",
                "/poExceptionManagement": "PO Exception Management",
                "/admin/manage-users": "Admin",
                "/admin/manage-reason-codes": "Admin",
                "/reports": "Reports",
                "/kpimetrics": "KPI Metrics",
                "/alerts": "Alerts",
                "/supplieralerts": "Alerts",
                "/admin-announcements": "Announcements",
                "/supplier-announcements": "Announcements",
                "/admin-training": "Training Content",
                "/training": "Training Content",
                "/settingsConfig": "Settings & Config",
                "/forecast-publish-exclusion": "Settings & Config",
                "/run-forecast-extraction": "Scheduler",
                "/forecastHistoryPage": "Scheduler",
                "/forecast-extraction-schedule": "Forecast Extraction Calendar",
            };

            const matchedMenu = pathToMenuMap[location.pathname];
            if (matchedMenu) {
                setSelectedMenu(matchedMenu);
            }

            setMenuInitialized(true);
        }
        setMenuInitialized(false);
    }, [location.pathname, menuInitialized]);

    const handleFunctionChange = (value) => {
        if (typeof value === "object") {
            setSelectedFunction(
                `${value.group} -> ${value.item}`
            );
        } else {
            setSelectedFunction(value);
        }
    };
    const handlePriorityChange = (e) => {
        setSelectedPriority(e.target.value)
    }

    const submitTicketHandler = () => {
        setIsSubmitClicked(true)
    }
    const onClickBack = () => {
        setIsSubmitClicked(false);
        setSuccessMessage("")
    }
    const onClickCancel = () => {
        resetForm();
        onClickBack()
        props.handleClose();
    }

    const createTicketAPI = async () => {
        const items = props?.selectedItem ?? [];
        const visibleFields = SELECTED_DATA_FIELDS.filter((field) =>
            items.some((row) => hasDisplayValue(pickValueByKeys(row, field.keys)))
        );

        const headerCells = visibleFields.map((field) => `<th>${field.label}</th>`).join('');
        const itemsHtml = items.map((row) =>
            `<tr>${visibleFields.map((field) => `<td>${pickValueByKeys(row, field.keys) ?? ""}</td>`).join('')}</tr>`
        ).join('');

        const issueDescription =
            items.length > 0 && visibleFields.length > 0
                ? `${description || ""}<br/><br/>Selected Items:<br/>
      <table border="1" cellspacing="0" cellpadding="4" style="width:100%; border-collapse:collapse; text-align:center;">
        <tr>${headerCells}</tr>
        ${itemsHtml}
      </table>`
                : description || "";

        const formData = new FormData();

        const supportTicketRequestBody = {
            userEmail: userEmail,
            description: description || "",
            function: selectedFunction,
            assigned: ASSIGNED_TO_VALUE,
            priority: selectedPriority,
            menu: selectedMenu,
            ...(items.length > 0 ? { forecastEntries: items } : {}),
        };

        const requestBlob = new Blob(
            [JSON.stringify(supportTicketRequestBody)],
            {
                type: "application/json",
            }
        );

        formData.append("request", requestBlob);

        if (uploadedFile) {
            formData.append("attachment", uploadedFile);
        }

        try {
            const responseSupportTicket = await dispatch(postSupportTicketSlice({
                dynamicEndpoint: `${postSupportTicketEndpoint}`,
                data: formData,
            }));
            if (
                responseSupportTicket.meta.requestStatus === "fulfilled" &&
                responseSupportTicket.payload?.status < 400
            ) {
                toast.success(responseSupportTicket.payload?.message || "Ticket created successfully");
            } else {
                toast.error(
                    responseSupportTicket.payload?.message || "Failed to create ticket",
                    {
                        autoClose: 2000,
                    }
                );
            }
        } catch (error) {
            console.error('API error:', error);
            toast.error(error, {
                autoClose: 2000,
            });
        } finally {
            onClickCancel(); // closes modal and resets form
        }
    }

    return (
        <Modal
            open={props.showModal}
            handleClose={onClickCancel}
            title={props.title}
            fullWidth={true}
            maxWidth="md"
            content={
                <>
                    {successMessage?.length > 0 ?
                        <div>
                            <span>{successMessage}</span>
                        </div>
                        :
                        !isSubmitClicked && successMessage?.length === 0 ?
                            <>
                                <Grid container spacing={2}>
                                    <Grid
                                        item
                                        xs={12}
                                        md={3}
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                        }}
                                    >
                                        <label className="label-position" htmlFor="hierarchy">
                                            Menu
                                        </label>
                                        <Dropdown
                                            maxWidth="100%"
                                            // label="Menu"
                                            value={selectedMenu || ""}
                                            name="menu"
                                            options={menuOptions}
                                            handleChange={(event) =>
                                                handleSelectedItemsChange("menu", event)
                                            }
                                        />
                                    </Grid>
                                    {/* <Grid
                                        item
                                        xs={12}
                                        md={2.5}
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                        }}
                                    >
                                        <label className="label-position" htmlFor="hierarchy">
                                            Sub Menu
                                        </label>
                                        <Dropdown
                                            maxWidth="100%"
                                            // label="Sub Menu"
                                            value={selectedCategory || ""}
                                            name="category"
                                            options={categoryOptions}
                                            handleChange={(event) =>
                                                handleSelectedItemsChange("category", event)
                                            }
                                        />
                                    </Grid> */}
                                    <Grid
                                        item
                                        xs={12}
                                        md={3}
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                        }}
                                    >
                                        <label className="label-position" htmlFor="function">
                                            Function
                                        </label>
                                        {isGrouped ? (
                                            <ExpandableFunctionDropdown
                                                value={selectedFunction}
                                                groups={functions}
                                                onChange={handleFunctionChange}
                                                disabled={!selectedMenu}
                                            />
                                        ) : (
                                            <Dropdown
                                                maxWidth="100%"
                                                value={selectedFunction}
                                                options={functions.map(item => ({
                                                    label: item,
                                                    value: item,
                                                    title: item,
                                                }))}
                                                handleChange={(e) => setSelectedFunction(e.target.value)}
                                                disabled={!selectedMenu}
                                            />
                                        )}
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        md={3}
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                        }}
                                    >
                                        <label className="label-position" htmlFor="hierarchy">
                                            Priority
                                        </label>

                                        <Dropdown
                                            maxWidth="100%"
                                            // label="Category"
                                            value={selectedPriority || ""}
                                            name="function"
                                            options={priorityOptions}
                                            handleChange={(event) =>
                                                handlePriorityChange(event)
                                            }
                                        />
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        md={3}
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "end",
                                        }}
                                    >
                                        <label className="label-position" htmlFor="assignedTo">
                                            Assigned To
                                        </label>
                                        <TextInput
                                            value={ASSIGNED_TO_VALUE}
                                            disabled={true}
                                        />
                                    </Grid>
                                </Grid>
                                {
                                    selectedMenu === "Planning" &&
                                    props?.selectedItem?.length > 0 &&
                                    renderSelectedItemsTable(props.selectedItem)
                                }
                                <Grid mt={2}>
                                    <TextareaAutosize
                                        minRows={6}
                                        maxLength={1000}
                                        placeholder="Issue Description"
                                        style={{ width: "100%" }}
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                    />

                                    <div
                                        style={{
                                            textAlign: "right",
                                            fontSize: "12px",
                                            color: description.length >= 1000 ? "red" : "#666",
                                            marginTop: "4px"
                                        }}
                                    >
                                        {description.length}/1000 characters
                                    </div>
                                </Grid>
                                {/* Upload Document */}
                                <Grid mt={2}>
                                    <label className="label-position">
                                        Upload Document
                                    </label>

                                    <TextField
                                        key={fileInputKey}
                                        fullWidth
                                        size="small"
                                        type="file"
                                        onChange={handleFileChange}
                                    />

                                    {uploadedFile && (
                                        <div
                                            style={{
                                                marginTop: "6px",
                                                fontSize: "12px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "12px",
                                            }}
                                        >
                                            <span style={{ color: "green" }}>
                                                Selected File: {uploadedFile.name}
                                            </span>

                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setUploadedFile(null);
                                                    setFileInputKey(Date.now());
                                                }}
                                                style={{
                                                    border: "none",
                                                    background: "transparent",
                                                    color: "red",
                                                    cursor: "pointer",
                                                    padding: 0,
                                                    fontSize: "12px",
                                                }}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    )}
                                </Grid>
                            </>
                            :
                            <>
                                <div>
                                    <p>Preview</p>
                                    <div style={{ border: '1px solid #bcbcbc', padding: 10, }}>
                                        {selectedMenu &&
                                            <div style={{ display: 'flex', margin: '10px 0px' }}>
                                                <b style={{ width: '25%' }}>Menu </b>
                                                <span style={{ width: '70%' }}>: {selectedMenu}</span>
                                            </div>
                                        }
                                        {/* {selectedCategory &&
                                            <div style={{ display: 'flex', margin: '10px 0px' }}>
                                                <b style={{ width: '25%' }}>Sub Menu</b>
                                                <span style={{ width: '70%' }}>: {selectedCategory}</span>
                                            </div>
                                        } */}
                                        {selectedFunction &&
                                            <div style={{ display: 'flex', margin: '10px 0px' }}>
                                                <b style={{ width: '25%' }}>Function</b>
                                                <span style={{ width: '70%' }}>: {selectedFunction}</span>
                                            </div>
                                        }
                                        {selectedPriority &&
                                            <div style={{ display: 'flex', margin: '10px 0px' }}>
                                                <b style={{ width: '25%' }}>Priority</b>
                                                <span style={{ width: '70%' }}>: {selectedPriority}</span>
                                            </div>
                                        }
                                        <div style={{ display: 'flex', margin: '10px 0px' }}>
                                            <b style={{ width: '25%' }}>Assigned To </b>
                                            <span style={{ width: '70%' }}>: {ASSIGNED_TO_VALUE}</span>
                                        </div>
                                        {(description || props?.selectedItem?.length > 0) &&
                                            <div style={{ display: 'flex', margin: '10px 0px' }}>
                                                <b style={{ width: '25%' }}>Issue Description </b>
                                                <span style={{ width: '70%' }}>:  {props?.selectedItem?.length > 0 ? <span><span style={{ fontWeight: 'bold' }}>User Remarks</span> <span> {merged}</span></span> : description}</span>
                                            </div>
                                        }
                                        {uploadedFile && (
                                            <div style={{ display: 'flex', margin: '10px 0px' }}>
                                                <b style={{ width: '25%' }}>Attachment</b>
                                                <span
                                                    style={{
                                                        width: '70%',
                                                        wordBreak: 'break-word'
                                                    }}
                                                >
                                                    : {uploadedFile.name}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                            </>

                    }
                </>
            }
            action={
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        margin: "15px 0",
                    }}
                >
                    <div>
                        {
                            successMessage?.length === 0 && <TextInput
                                label="Initiator"
                                value={props.userEmail}
                                disabled={true}
                            />
                        }
                    </div>
                    {
                        successMessage?.length > 0 ?
                            <ButtonComponent
                                maxWidth="150px"
                                label="Close"
                                bgColor="white"
                                color="black"
                                onClick={onClickCancel}
                            />
                            :
                            <div
                                style={{
                                    display: "flex",
                                    gap: 10,
                                    justifyContent: 'end',
                                    width: "300px",
                                    alignItems: "center",
                                }}
                            >
                                {!isSubmitClicked && successMessage?.length === 0 ?
                                    <ButtonComponent
                                        maxWidth="150px"
                                        label="Cancel"
                                        bgColor="white"
                                        color="black"
                                        onClick={onClickCancel}
                                    />
                                    :
                                    stateSelector?.LTSupportTicketSlice?.loading ?
                                        <></>
                                        :
                                        <ButtonComponent
                                            maxWidth="150px"
                                            label="Back"
                                            bgColor="white"
                                            color="black"
                                            onClick={onClickBack}
                                        />
                                }
                                {!isSubmitClicked && successMessage?.length === 0 ?
                                    <ButtonComponent
                                        maxWidth="150px"
                                        label="Submit"
                                        bgColor="black"
                                        color="white"
                                        onClick={submitTicketHandler}
                                        disabled={!isSubmitEnabled}
                                    />
                                    :
                                    stateSelector?.LTSupportTicketSlice?.loading ?
                                        <Box style={{ textAlign: 'center' }}>
                                            <CircularProgress color="inherit" style={{ width: '20px', height: '20px', textAlign: 'center', marginRight: 30 }} />
                                        </Box>
                                        :
                                        <ButtonComponent
                                            maxWidth="250px"
                                            label={createTicketLoading.loading ? "Creating..." : "Create Ticket"}
                                            bgColor="black"
                                            color="white"
                                            disabled={createTicketLoading.loading}
                                            onClick={createTicketAPI}
                                        />
                                }
                            </div>
                    }
                </div>
            }
        />
    );
};

export default SupportTicketModel;